import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { ExtendedFAB, Icon, LoadingInner, TableInner } from '@chocolate-soup-inc/cs-frontend-components';
import { RowSelectionState } from '@tanstack/react-table';
import clsx from 'clsx';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { BrickColor } from '../../components/brickColor/BrickColor';
import { CountryName } from '../../components/coutryName/CountryName';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import { TablePage, TTablePageRef } from '../../components/tablePage/TablePage';
import tableStyles from '../../components/tablePage/TablePage.module.scss';
import { useFilter } from '../../contexts/filters';
import { isAddressValid } from '../../entities/addresses/shared';
import { TEventType, useClientOneOffUpcomingEvents } from '../../entities/events/queries';
import { ONE_OFF_EVENT_TYPES, translateEventType } from '../../entities/events/shared';
import { calculatedShippingDate } from '../../entities/gifts/shared';
import { TEventTypes, useCreateEventGiftMutation, useDeleteEventMutation, useSkipEventMutation } from '../../generated/graphql';
import { useInfiniteScrollQuery } from '../../hooks/InitinityScroll';
import styles from './Events.module.scss';
import { useEventsFilters } from './_shared';

export const ClientOneOffEvents = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tableRef = useRef<TTablePageRef>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [includingNextDays, setIncludingNextDays] = useState(60);
  const { data: events, error, loading, nextToken, fetchMore, refetch } = useClientOneOffUpcomingEvents({ includingNextDays });

  const { sentinelRef } = useInfiniteScrollQuery({
    rootRef: tableContainerRef,
    loading,
    queries: [{ fetchMore, nextToken }]
  });

  const {
    companies,
    companiesCounts,
    companyFilter,
    filterByCompany,
    setCompany,
    months,
    monthFilter,
    filterByMonth,
    setMonth,
    eventTypeFilter,
    setEventTypeFilter,
    filterByEventType,
    recipientNameFilter,
    setRecipientNameFilter,
    filterByRecipientName
  } = useEventsFilters({ events });

  const filteredEvents = useMemo(
    () => events.filter((event) => filterByCompany(event) && filterByMonth(event) && filterByEventType(event) && filterByRecipientName(event)),
    [events, filterByCompany, filterByEventType, filterByMonth, filterByRecipientName]
  );

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);
  const [createGiftsLoading, setCreateGiftsLoading] = useState(false);
  const actionsLoading = deleteLoading || skipLoading || createGiftsLoading;

  const [deleteEvent] = useDeleteEventMutation();
  const [skipEvent] = useSkipEventMutation();
  const [createEventGift] = useCreateEventGiftMutation();

  const selectedEvents = useMemo(() => _.compact(selectedRows.map((id) => events.find((event) => event.id === id))), [events, selectedRows]);
  const selectedEventsHaveValidAddresses = useMemo(() => selectedEvents.every((event) => isAddressValid(event.address)), [selectedEvents]);

  const deleteEvents = useCallback(async () => {
    setDeleteLoading(true);
    let recordId: string | undefined;
    try {
      for (const event of selectedEvents) {
        recordId = event.id;
        await deleteEvent({
          variables: {
            companyId: event.companyId,
            id: event.id,
            version: event._version
          }
        });
      }
      await refetch();
      toast.success(`${selectedEvents.length} client one-off event${selectedEvents.length === 1 ? '' : 's'} deleted.`);
      setSelectedRows([]);
    } catch (error) {
      console.error('Client event deleteEvents failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error deleting the selected client one-off events.');
    } finally {
      setDeleteLoading(false);
      tableRef.current?.resetConfirmationModal();
    }
  }, [deleteEvent, refetch, selectedEvents, tableRef]);

  const skipEvents = useCallback(async () => {
    setSkipLoading(true);
    let recordId: string | undefined;
    try {
      for (const event of selectedEvents) {
        recordId = event.id;
        await skipEvent({
          variables: {
            companyId: event.companyId,
            id: event.id,
            version: event._version
          }
        });
      }
      await refetch();
      setSelectedRows([]);
    } catch (error) {
      console.error('Client event skipEvents failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error skipping the selected client one-off events.');
    } finally {
      setSkipLoading(false);
      tableRef.current?.resetConfirmationModal();
    }
  }, [refetch, selectedEvents, skipEvent, tableRef]);

  const createGifts = useCallback(async () => {
    if (!selectedEventsHaveValidAddresses) {
      toast.error('Resolve the missing address data before creating gifts for the selected events.');
      tableRef.current?.resetConfirmationModal();
      return;
    }

    setCreateGiftsLoading(true);
    let recordId: string | undefined;
    try {
      for (const event of selectedEvents) {
        recordId = event.id;
        await createEventGift({
          variables: {
            eventId: event.id,
            companyId: event.companyId,
            eventVersion: event._version,
            shippingDate: calculatedShippingDate({
              date: event.eventDate,
              address: {
                country: event.address?.country as string,
                state: event.address?.state as string
              }
            })
          }
        });
      }
      await refetch();
      setSelectedRows([]);
    } catch (error) {
      console.error('Client event createGifts failed', { eventId: recordId, error: serializeError(error) });
      toast.error('There was an error creating gifts for the selected client one-off events.');
    } finally {
      setCreateGiftsLoading(false);
      tableRef.current?.resetConfirmationModal();
    }
  }, [createEventGift, refetch, selectedEvents, selectedEventsHaveValidAddresses, tableRef]);

  const onDeleteClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Delete ${selectedRows.length} Event${selectedRows.length === 1 ? '' : 's'}?`,
      supportingText: 'The selected client one-off events will be permanently deleted.',
      confirmLabel: 'Delete',
      onConfirmClick: deleteEvents,
      open: true
    });
  }, [deleteEvents, selectedRows.length, tableRef]);

  const onSkipClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Skip ${selectedRows.length} Event${selectedRows.length === 1 ? '' : 's'}?`,
      supportingText: 'The selected client one-off events will be skipped.',
      confirmLabel: 'Skip',
      onConfirmClick: skipEvents,
      open: true
    });
  }, [selectedRows.length, skipEvents, tableRef]);

  const onCreateGiftsClick = useCallback(() => {
    tableRef.current?.setConfirmationProps({
      headline: `Create ${selectedRows.length} Gift${selectedRows.length === 1 ? '' : 's'}?`,
      supportingText: 'Gifts will be created for the selected client one-off events.',
      confirmLabel: 'Create',
      onConfirmClick: createGifts,
      open: true
    });
  }, [createGifts, selectedRows.length, tableRef]);

  const filters = useMemo<TFiltersProps['filters']>(
    () => [
      {
        type: 'singleSelect',
        label: 'Date',
        name: 'date',
        onChange: setMonth,
        options: months.map((month) => ({ label: month, value: month })),
        value: monthFilter
      },
      {
        disabled: companies.length === 0,
        type: 'singleSelect',
        label: 'Company',
        name: 'companyId',
        onChange: setCompany,
        options: companies.map((company) => ({
          label: `${company.name} (${companiesCounts[company.id]})`,
          value: company.id
        })),
        value: companyFilter
      },
      {
        type: 'textInput',
        label: 'Recipient Name',
        name: 'recipientName',
        onChange: (value) => setRecipientNameFilter(value != null ? String(value) : undefined),
        value: recipientNameFilter
      },
      {
        type: 'singleSelect',
        label: 'Event Type',
        name: 'eventType',
        onChange: (value) => setEventTypeFilter(value != null ? (value as TEventTypes) : undefined),
        options: Object.entries(ONE_OFF_EVENT_TYPES).map(([value, label]) => ({ label, value })),
        value: eventTypeFilter
      },
      {
        includeEmptyOption: false,
        type: 'singleSelect',
        label: 'Including Next Days',
        name: 'includingNextDays',
        onChange: (value) => setIncludingNextDays(value != null ? Number(value) : 60),
        options: [
          { label: '60 Days', value: 60 },
          { label: '90 Days', value: 90 },
          { label: 'All Days', value: 999 }
        ],
        value: includingNextDays
      }
    ],
    [companies, companiesCounts, companyFilter, eventTypeFilter, includingNextDays, monthFilter, months, recipientNameFilter, setCompany, setEventTypeFilter, setMonth, setRecipientNameFilter]
  );

  const getRowId = useCallback((event: TEventType) => event.id, []);
  const onRowSelectionChange = useCallback((selection: RowSelectionState) => {
    setSelectedRows(Object.keys(selection));
  }, []);
  const getRowClassName = useCallback((row: { original: TEventType }) => {
    if (!row.original.address || !row.original.recipient) return styles.noAddressRow;
  }, []);

  return (
    <TablePage actionsLoading={actionsLoading} error={error} ref={tableRef} title='Client One-off'>
      <Filters filters={filters} />
      <div ref={tableContainerRef} style={{ overflow: 'auto' }}>
        <TableInner<TEventType>
          data={filteredEvents}
          defaultSorting={{ id: 'eventDate', desc: false }}
          emptyText={events.length === 0 ? 'No upcoming client one-off events.' : 'No upcoming client one-off events for the current filters.'}
          enableSorting={true}
          fillSelectedRows={true}
          fixedHeader={true}
          getRowClassName={getRowClassName}
          getRowId={getRowId}
          hoverableRows={!actionsLoading}
          onRowSelectionChange={onRowSelectionChange}
          selectableRows={true}
          virtual={false}
          columns={[
            {
              header: 'Company Name',
              accessorKey: 'company.name'
            },
            {
              header: 'Recipient Name',
              accessorKey: 'recipient.fullName'
            },
            {
              header: 'Event Type',
              accessorKey: 'type',
              cell: ({ cell }) => (
                <div className={tableStyles.tableSpaceTertiary}>
                  <span>{cell.row.original.type === TEventTypes.Other || cell.row.original.type === TEventTypes.CustomBricks ? cell.row.original.customType : translateEventType(cell.row.original.type)}</span>
                </div>
              )
            },
            {
              header: 'Event Date',
              accessorKey: 'eventDate',
              cell: ({ cell }) => <span>{readableDate(cell.row.original.eventDate)}</span>
            },
            {
              header: 'Brick Quantity',
              accessorKey: 'brickQuantity',
              cell: ({ cell }) => <span>{cell.row.original.brickQuantity ?? 1}</span>
            },
            {
              header: 'Brick Size',
              accessorKey: 'brickSize',
              cell: ({ cell }) => <span>{cell.row.original.brickSize || '-'}</span>
            },
            {
              header: 'Brick Color',
              accessorKey: 'brickColor',
              cell: ({ cell }) => <BrickColor value={cell.row.original.brickColor} />
            },
            {
              header: 'Brick Text',
              accessorKey: 'brickText',
              cell: ({ cell }) => <span style={{ whiteSpace: 'pre-wrap' }}>{cell.row.original.brickText || '-'}</span>
            },
            {
              header: 'Notes',
              accessorKey: 'notes',
              cell: ({ cell }) => <span>{cell.row.original.notes || '-'}</span>
            },
            {
              header: 'Country',
              accessorKey: 'address.country',
              cell: ({ cell }) =>
                isAddressValid(cell.row.original.address) ? (
                  <CountryName name={cell.row.original.address?.country || undefined} />
                ) : (
                  <div className={clsx(styles.missingAddress, tableStyles.tableSpacePrimary)}>
                    <Icon icon='warning' />
                    <span>Missing address data</span>
                  </div>
                )
            }
          ]}
        />
        {loading && <LoadingInner className={tableStyles.contentLoader} />}
        {nextToken && <div ref={sentinelRef} style={{ height: '20px' }} />}
      </div>
      {selectedRows.length > 0 && (
        <FloatingActions>
          <ExtendedFAB disabled={actionsLoading} label={`Delete ${selectedRows.length} event${selectedRows.length === 1 ? '' : 's'}`} leadingIcon='delete' loading={deleteLoading} onClick={onDeleteClick} variant='tertiary' />
          <ExtendedFAB disabled={actionsLoading} label={`Skip ${selectedRows.length} event${selectedRows.length === 1 ? '' : 's'}`} leadingIcon='exit_to_app' loading={skipLoading} onClick={onSkipClick} variant='secondary' />
          <ExtendedFAB
            disabled={actionsLoading || !selectedEventsHaveValidAddresses}
            label={`Create ${selectedRows.length} gift${selectedRows.length === 1 ? '' : 's'}`}
            leadingIcon='add'
            loading={createGiftsLoading}
            onClick={onCreateGiftsClick}
            variant='surface'
          />
        </FloatingActions>
      )}
    </TablePage>
  );
};
